import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { unavailableGuard } from './core/guards/unavailable.guard';
import { adminGuard } from './core/guards/admin.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { managerGuard } from './core/guards/manager.guard';
import { pendingGuard } from './core/guards/pending.guard';
import { congressRedirectGuard } from './core/guards/congress-redirect.guard';
import { CongressSelectorService } from './core/services/congress-selector.service';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/login/login').then((m) => m.Login),
  },
  {
    // Formulaire des comptes `local`, hors de la page de connexion, qui ne propose qu'Azure AD.
    path: 'login/admin',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/login/admin-login').then((m) => m.AdminLogin),
  },
  {
    path: 'unavailable',
    canActivate: [unavailableGuard],
    loadComponent: () =>
      import('./features/service-unavailable/service-unavailable').then(
        (m) => m.ServiceUnavailable,
      ),
  },
  {
    path: 'pending',
    canActivate: [pendingGuard],
    loadComponent: () =>
      import('./features/pending-approval/pending-approval').then((m) => m.PendingApproval),
  },
  {
    path: 'admin',
    canActivate: [authGuard, adminGuard],
    loadComponent: () => import('./features/admin/admin').then((m) => m.Admin),
    children: [
      {
        path: '',
        loadComponent: () => import('./features/admin/users/admin-users').then((m) => m.AdminUsers),
      },
      {
        path: 'parametres',
        loadComponent: () =>
          import('./features/admin/parametres/admin-parametres').then((m) => m.AdminParametres),
      },
      {
        path: 'affichage',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/affichage/admin-affichage').then((m) => m.AdminAffichage),
      },
      {
        path: 'versions',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/versions/admin-versions').then((m) => m.AdminVersions),
      },
      {
        path: 'general',
        loadComponent: () =>
          import('./features/admin/general/admin-general').then((m) => m.AdminGeneral),
      },
      {
        path: 'recompute',
        loadComponent: () =>
          import('./features/admin/recompute/admin-recompute').then((m) => m.AdminRecompute),
      },
    ],
  },
  {
    // Charge la liste des congrès puis redirige vers l'édition par défaut.
    path: '',
    pathMatch: 'full',
    canActivate: [authGuard, congressRedirectGuard],
    children: [],
  },
  {
    // Le congrès courant vit dans l'URL => deep-link, refresh et partage fonctionnent.
    path: ':congressId',
    canActivate: [authGuard],
    resolve: { congres: () => inject(CongressSelectorService).load() },
    loadComponent: () => import('./layout/shell/shell').then((m) => m.Shell),
    children: [
      {
        path: '',
        loadComponent: () => import('./features/cockpit/cockpit').then((m) => m.Cockpit),
      },
      {
        path: 'cadencement',
        loadComponent: () =>
          import('./features/cadencement/cadencement').then((m) => m.Cadencement),
      },
      {
        path: 'profil',
        loadComponent: () => import('./features/profile/profile').then((m) => m.Profile),
      },
      {
        // Aide « Méthodologie & calculs » : LECTURE POUR TOUS (pas de guard) — documente
        // comment chaque bloc est calculé (règles, colonnes, cas limites).
        path: 'aide',
        loadComponent: () => import('./features/help/help').then((m) => m.Help),
      },
      {
        path: 'segments',
        loadComponent: () =>
          import('./features/segment-analysis/segment-analysis').then((m) => m.SegmentAnalysis),
      },
      {
        path: 'reporting',
        loadComponent: () => import('./features/reporting/reporting').then((m) => m.Reporting),
      },
      {
        path: 'industry-partners',
        loadComponent: () =>
          import('./features/industry-partners/industry-partners').then((m) => m.IndustryPartners),
      },
      {
        // La clé du partner arrive en query param `p` (les noms contiennent des « / », donc
        // jamais en segment d'URL), bindée en `input()` par `withComponentInputBinding()`.
        path: 'industry-partners/fiche',
        loadComponent: () =>
          import('./features/industry-partners/detail/partner-detail').then((m) => m.PartnerDetail),
      },
      {
        path: 'settings',
        canActivate: [managerGuard],
        loadComponent: () =>
          import('./features/target-settings/target-settings').then((m) => m.TargetSettings),
      },
      {
        // Historique du paramétrage : LECTURE POUR TOUS (pas de adminGuard) — la
        // transparence des changements de référence fait partie de la confiance.
        path: 'settings/history',
        loadComponent: () =>
          import('./features/settings-history/settings-history').then((m) => m.SettingsHistory),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
